import { APP_ID, APP_INITIALIZER, ApplicationConfig, ErrorHandler, LOCALE_ID, PLATFORM_ID, Provider, importProvidersFrom, inject } from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NoPreloading, PreloadAllModules, Router, RouteReuseStrategy, provideRouter, withEnabledBlockingInitialNavigation, withNavigationErrorHandler, withPreloading, withRouterConfig } from '@angular/router';
import { setAssetPath } from 'ionicons/components';
import { DOCUMENT, DatePipe, isPlatformBrowser, registerLocaleData } from '@angular/common';
import localeRu from '@angular/common/locales/ru';
import localePl from '@angular/common/locales/pl';
import localeTh from '@angular/common/locales/th';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { Socket } from 'ngx-socket-io';
import { appRoutes } from './app.routes';
import { AuthInterceptor } from '@interceptors/auth.interceptor';
import { SupportRequestTimeoutInterceptor } from '@interceptors/support-request-timeout.interceptor';
import { environment } from '@environments/environment';
import { AuthService } from '@services/auth.service';
import { Client } from '@api/client';
import { ExtendedClient } from '@api/extended-client';
import { WebSocketController } from '@services/web-socket.controller';
import { TraceService } from '@sentry/angular';
import { MissingTranslationHandler, TranslateCompiler, TranslateModule, TranslateModuleConfig, TranslateService } from '@ngx-translate/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import type { IonicConfig } from '@ionic/core';
import { CombinedErrorHandlerService } from '@services/error-handlers/combined-error-handler.service';
import { MyMissingTranslationHandler, TranslateLoaderProvider } from '@utils/translate-provider';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { ConfigService } from '@services/config.service';
import { Diagnostic } from '@awesome-cordova-plugins/diagnostic/ngx';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PlatformService } from '@services/platform.service';
import { StatusBarService } from '@services/status-bar.service';
import { firstValueFrom } from 'rxjs';
import { StateOverlayService, AppNavigationService } from '@state-routing';
import { SplashScreen } from '@capacitor/splash-screen';

registerLocaleData(localeRu);
registerLocaleData(localePl);
registerLocaleData(localeTh);

const SentryAndTelegramErrorHandlerProviders: Provider = {
  provide: ErrorHandler,
  useClass: CombinedErrorHandlerService
};

const SentryTraceService: Provider = {
  provide: TraceService,
  deps: [Router],
};

const SentryAppInitializer: Provider = {
  provide: APP_INITIALIZER,
  useFactory: () => (): void => {
  },
  deps: [TraceService],
  multi: true,
};

const RouteStrategyProvider: Provider = {
  provide: RouteReuseStrategy,
  useClass: IonicRouteStrategy
};

const LocaleIdProvider: Provider = {
  provide: LOCALE_ID,
  deps: [TranslateService],
  useFactory: (languageService: TranslateService): string => {
    const langs: string[] = environment.activeLocales;
    const lang: string = languageService.currentLang || languageService.defaultLang;
    return lang
      ? langs.find((l: string) => lang.startsWith(l))
      : 'en-US';
  }
};

const AuthInterceptorProvider: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: AuthInterceptor,
  multi: true
};

const ApiInitializerProvider: Provider = {
  provide: APP_INITIALIZER,
  useFactory: (
    authService: AuthService,
    webSocketController: WebSocketController,
    configService: ConfigService,
    client: Client,
    socket: Socket,
    platformService: PlatformService,
    statusBarService: StatusBarService,
  ) => async (): Promise<void> => {
    configService.initializeConfig();
    console.log('[Init] Applying API base URL...');
    client['baseUrl'] = configService.urlBackEndApi;
    console.log('[Init] API base URL:', client['baseUrl']);
    console.log('[Init] Applying Socket URL...');
    socket['ioSocket'].io.uri = configService.socketConfig.url;
    console.log('[Init] Socket URL:', socket['ioSocket'].io.uri);

    if (platformService.isAndroidApp) {
      statusBarService.setBackgroundColor().catch((error: unknown) => console.error('Status bar init error', error));
    }
    await authService.init();

    if (!platformService.isSSR) {
      await webSocketController.init();
    }
  },
  deps: [
    AuthService,
    WebSocketController,
    ConfigService,
    Client,
    Socket,
    PlatformService,
    StatusBarService,
  ],
  multi: true
};

const TranslationInitializer: Provider = {
  provide: APP_INITIALIZER,
  useFactory: (translate: TranslateService) => {
    return () => {
      const defaultLang = environment.defaultLanguage;
      translate.setDefaultLang(defaultLang);
      return firstValueFrom(translate.use(defaultLang));
    };
  },
  deps: [TranslateService],
  multi: true,
};

const translateModuleConfig: TranslateModuleConfig = {
  loader: TranslateLoaderProvider,
  missingTranslationHandler: {provide: MissingTranslationHandler, useClass: MyMissingTranslationHandler},
  useDefaultLang: true,
  compiler: {
    provide: TranslateCompiler,
    useClass: TranslateMessageFormatCompiler
  }
};

const SocketProvider: Provider = {
  provide: Socket,
  useFactory: (config: ConfigService) => new Socket(config.socketConfig),
  deps: [ConfigService]
};

const IonicAssetsInitializer: Provider = {
  provide: APP_INITIALIZER,
  useFactory: (document: Document, platformId: Object) => (): void => {
    if (isPlatformBrowser(platformId)) {
      // Keep named icons, including backend-provided names, loading from the copied SVG assets.
      setAssetPath(document.baseURI);
    }
  },
  deps: [DOCUMENT, PLATFORM_ID],
  multi: true,
};

const ionicConfig: IonicConfig = {
  innerHTMLTemplatesEnabled: true,
  swipeBackEnabled: false,
  backButtonDefaultHref: '/avatar',
};

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: APP_ID, useValue: 'serverApp' },
    provideRouter(
      appRoutes,
      withRouterConfig({canceledNavigationResolution: 'computed'}),
      withPreloading(environment.production ? NoPreloading : PreloadAllModules),
      withEnabledBlockingInitialNavigation(),
      withNavigationErrorHandler((): void => {
        if (!inject(PlatformService).isDevice) return;

        void SplashScreen.hide().catch((error: unknown): void => console.error('Splash screen hide failed', error));
      }),
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    importProvidersFrom(TranslateModule.forRoot(translateModuleConfig)),
    provideIonicAngular(ionicConfig),
    IonicAssetsInitializer,
    TranslationInitializer, // required for tag translations in SSR mode!
    SentryAppInitializer,
    SentryTraceService,
    SentryAndTelegramErrorHandlerProviders,
    RouteStrategyProvider,
    LocaleIdProvider,
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideMessaging(() => getMessaging()),
    Client,
    ExtendedClient,
    SocketProvider,
    {provide: HTTP_INTERCEPTORS, useClass: SupportRequestTimeoutInterceptor, multi: true},
    AuthInterceptorProvider,
    ApiInitializerProvider,
    StateOverlayService,
    AppNavigationService,
    Diagnostic,
    DatePipe,
  ],
};
